/**
 * Dashboard: how the recipe catalog is distributed across protein, cuisine, carb, method, time,
 * diets, allergens and tags, with the combinations, the coverage gaps and the data problems.
 *
 * The numbers come from js/catalogDistribution.js; this file only draws them. Clicking a bar,
 * a heatmap cell or a gap adds a filter, and every panel re-renders against the filtered slice.
 * A panel ignores its own dimension's filter, so the chosen value stays visible beside the rest.
 */
import {
  DIMENSION_BY_KEY,
  buildTaxonomy,
  countBy,
  coverageGaps,
  crossTab,
  dataQuality,
  evaluateGoals,
  filterRecipes,
  goalMarkers,
  labelFor,
  summarize,
  timeHistogram
} from "./catalogDistribution.js";
import { BADGE, BUTTON } from "./ui/classes.js";
import { solidIcon } from "./ui/icons.js";

const BAR_PANELS = [
  { key: "protein", note: "classification.primary_protein" },
  { key: "cuisine", note: "classification.cuisine" },
  { key: "carb", note: "classification.carb_category" },
  { key: "method", note: "classification.method" },
  { key: "time", note: "total_time_minutes", histogram: true },
  { key: "diet", note: "Derived from ingredients. A recipe can suit several." },
  { key: "allergen", note: "Derived from ingredients. A recipe can contain several." },
  { key: "effort", note: "classification.time_effort" },
  { key: "lifestyle", note: "classification.goals_lifestyles" },
  { key: "occasion", note: "classification.occasions_seasons" }
];

const CROSS_TAB_KEYS = ["protein", "cuisine", "carb", "method", "time", "diet", "allergen", "effort", "lifestyle", "access", "status"];

const state = {
  recipes: [],
  taxonomy: {},
  filters: {},
  crossRow: "protein",
  crossCol: "cuisine",
  // globals/coverage_targets: undefined until its listener answers, null when it doesn't exist or can't be read.
  coverageTargets: undefined,
  goals: [],
  markers: {},
  hasData: false
};

let listenersBound = false;

export async function loadDashboardTemplate() {
  const container = document.getElementById("sectionDashboardContainer");
  if (!container || document.getElementById("sectionDashboard")) return;

  try {
    const resp = await fetch("templates/dashboard.html");
    if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
    container.innerHTML = await resp.text();
  } catch (err) {
    console.error("❌ Dashboard template load error:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the dashboard template: ${escapeHtml(err.message)}</div>`;
    return;
  }
  bindListeners();
  if (state.hasData) render();
}

/**
 * Called by app.js whenever the catalog summary, the categories or the coverage targets change.
 * `catalogRecipes` is `globals/admin_catalog_summary.recipes`; `categoriesData` is `globals/categories`;
 * `coverageTargets` is `globals/coverage_targets` (published by recipe-builder's sync), null when missing.
 */
export function renderDashboardStats(catalogRecipes = {}, categoriesData = {}, coverageTargets = undefined) {
  state.recipes = Object.entries(catalogRecipes || {}).map(([id, r]) => ({ ...r, id: r?.id || r?.recipe_id || id }));
  state.taxonomy = buildTaxonomy(categoriesData || {});
  state.coverageTargets = coverageTargets;
  state.goals = coverageTargets?.targets ? evaluateGoals(state.recipes, coverageTargets.targets) : [];
  state.markers = goalMarkers(state.goals);
  state.hasData = true;
  if (document.getElementById("sectionDashboard")) {
    bindListeners();
    render();
  }
}

function bindListeners() {
  const root = document.getElementById("sectionDashboard");
  if (!root || listenersBound) return;
  listenersBound = true;

  root.addEventListener("click", (e) => {
    const toggle = e.target.closest("[data-dash-filter]");
    if (toggle) {
      toggleFilter(toggle.dataset.dashFilter, toggle.dataset.value);
      return;
    }
    const set = e.target.closest("[data-dash-set]");
    if (set) {
      setFilter(set.dataset.dashSet, set.dataset.value);
      return;
    }
    const cell = e.target.closest("[data-dash-cell]");
    if (cell) {
      state.filters[state.crossRow] = cell.dataset.row;
      state.filters[state.crossCol] = cell.dataset.col;
      render();
      return;
    }
    const remove = e.target.closest("[data-dash-remove]");
    if (remove) {
      delete state.filters[remove.dataset.dashRemove];
      render();
      return;
    }
    if (e.target.closest("[data-dash-clear]")) {
      state.filters = {};
      render();
      return;
    }
    const recipe = e.target.closest("[data-recipe-id]");
    if (recipe && window.crmApp?.viewRecipe) {
      window.crmApp.viewRecipe(recipe.dataset.recipeId);
    }
  });

  root.addEventListener("change", (e) => {
    if (e.target.id === "dashCrossRow") state.crossRow = e.target.value;
    else if (e.target.id === "dashCrossCol") state.crossCol = e.target.value;
    else return;
    renderCrossTab(filterRecipes(state.recipes, state.filters));
  });
}

function toggleFilter(dimKey, value) {
  if (!DIMENSION_BY_KEY[dimKey]) return;
  if (state.filters[dimKey] === value) delete state.filters[dimKey];
  else state.filters[dimKey] = value;
  render();
}

function setFilter(dimKey, value) {
  if (!DIMENSION_BY_KEY[dimKey]) return;
  if (value) state.filters[dimKey] = value;
  else delete state.filters[dimKey];
  render();
}

// Recipes matching every filter except the one on `dimKey`.
function sliceWithout(dimKey) {
  const { [dimKey]: _ignored, ...rest } = state.filters;
  return filterRecipes(state.recipes, rest);
}

function render() {
  if (!document.getElementById("sectionDashboard")) return;
  const slice = filterRecipes(state.recipes, state.filters);

  renderFilterBar();
  renderKpis(slice);
  renderGoals();
  renderPanels();
  renderCrossTab(slice);
  renderGaps(slice);
  renderQuality(slice);
}

// Tailwind class lists for what this file draws (#1209); whole strings, so Tailwind finds them.
const EMPTY = "px-4 py-8 text-center text-sm text-gray-500 sm:px-6 dark:text-gray-400";
const CARD = "mb-6 break-inside-avoid rounded-lg bg-white shadow-xs outline-1 outline-black/5 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10";
const PILL = "inline-flex items-center gap-x-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-white/10 dark:text-gray-300";
const PILL_LINK = "inline-flex items-center gap-x-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 hover:bg-paprika-50 hover:text-paprika-700 dark:bg-white/10 dark:text-gray-300 dark:hover:bg-paprika-500/15 dark:hover:text-paprika-300";
const STATUS = { warning: BADGE.yellow, critical: BADGE.red, info: BADGE.blue };
// A panel's note is a field path (monospace) or a sentence.
const NOTE = "mt-0.5 text-xs text-gray-500 dark:text-gray-400";

// A closed section's header shows its count (#1623).
function setSummary(id, html) {
  const el = document.getElementById(id);
  if (el) el.innerHTML = html;
}

const SUMMARY_NUM = "font-semibold text-gray-900 tabular-nums dark:text-white";

function emptyHtml(text) {
  return `<p class="${EMPTY}">${text}</p>`;
}

function renderFilterBar() {
  document.querySelectorAll("#sectionDashboard [data-dash-set]").forEach((btn) => {
    const active = (state.filters[btn.dataset.dashSet] || "") === btn.dataset.value;
    btn.setAttribute("aria-pressed", String(active));
  });

  const chips = document.getElementById("dashChips");
  if (!chips) return;
  const entries = Object.entries(state.filters).filter(([k]) => k !== "status" && k !== "access");
  if (entries.length === 0) {
    chips.innerHTML = `<span class="text-sm text-gray-500 dark:text-gray-400">Click any bar or cell to filter every panel by it.</span>`;
    return;
  }
  chips.innerHTML = entries.map(([k, v]) => `
      <button type="button" data-dash-remove="${escapeAttr(k)}" aria-label="Remove filter ${escapeAttr(DIMENSION_BY_KEY[k].label)}: ${escapeAttr(labelFor(k, v, state.taxonomy))}" class="inline-flex items-center gap-x-1.5 rounded-md bg-paprika-50 px-2 py-1 text-sm font-medium text-paprika-800 inset-ring inset-ring-paprika-600/20 hover:bg-paprika-100 dark:bg-paprika-500/10 dark:text-paprika-200 dark:inset-ring-paprika-400/30 dark:hover:bg-paprika-500/20">
        <span class="text-xs font-normal text-paprika-700/80 dark:text-paprika-300/80">${escapeHtml(DIMENSION_BY_KEY[k].label)}</span>
        ${escapeHtml(labelFor(k, v, state.taxonomy))}
        ${solidIcon("x-mark", "size-4 text-paprika-500")}
      </button>`).join("")
    + `<button type="button" data-dash-clear class="${BUTTON.link} text-sm">Clear all</button>`;
}

function renderKpis(slice) {
  const el = document.getElementById("dashKpis");
  if (!el) return;
  if (state.recipes.length === 0) {
    el.innerHTML = `<p class="col-span-full ${EMPTY}">No recipes loaded yet (globals/admin_catalog_summary).</p>`;
    return;
  }
  const s = summarize(slice, state.taxonomy);
  const pct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : "–");
  const topLine = (row, dimKey) => (row ? `Most: ${escapeHtml(labelFor(dimKey, row.value, state.taxonomy))} (${pct(row.count, s.total)})` : "—");

  const tiles = [
    {
      value: s.total,
      label: Object.keys(state.filters).length ? `Recipes in selection (of ${state.recipes.length})` : "Recipes",
      sub: `${s.published} published · ${s.drafts} draft`
    },
    { value: s.signature, label: "Signature (Savvy)", sub: `${pct(s.signature, s.total)} of the selection · ${s.free} free` },
    { value: s.medianMinutes === null ? "–" : `${s.medianMinutes} min`, label: "Median total time", sub: `${pct(s.under30, s.timed)} take 30 min or less` },
    {
      value: s.cuisinesKnown ? `${s.cuisinesUsed}/${s.cuisinesKnown}` : s.cuisinesUsed,
      label: "Cuisines covered",
      sub: topLine(s.topCuisine, "cuisine")
    },
    { value: s.proteinsUsed, label: "Primary proteins", sub: topLine(s.topProtein, "protein") }
  ];

  el.innerHTML = tiles.map((t) => `
    <dl class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow-xs outline-1 outline-black/5 sm:p-6 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10">
      <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">${escapeHtml(t.label)}</dt>
      <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">${escapeHtml(String(t.value))}</dd>
      <dd class="mt-1 text-sm text-gray-500 dark:text-gray-400">${t.sub}</dd>
    </dl>`).join("");
}

function goalTagLabel(goal) {
  const name = labelFor(goal.dimKey, goal.tag, state.taxonomy);
  return goal.without ? `Free of ${name.toLowerCase()}` : name;
}

// One decimal, so 9.9% against a 10% goal doesn't read as 10%.
function formatShare(share) {
  return `${(Math.round(share * 1000) / 10).toFixed(1)}%`;
}

function goalRangeText(min, max) {
  const p = (x) => `${Math.round(x * 100)}%`;
  if (min !== null && max !== null) return `${p(min)}–${p(max)}`;
  return min !== null ? `≥ ${p(min)}` : `≤ ${p(max)}`;
}

function goalActionText(goal) {
  if (goal.status === "met") return "";
  if (goal.needed === null) return "Can't be met by adding recipes";
  const n = `~${goal.needed} recipe${goal.needed === 1 ? "" : "s"}`;
  if (goal.status === "under") return goal.without ? `Add ${n} without it` : `Add ${n} with it`;
  return goal.without ? `Add ${n} that contain it` : `Add ${n} without it`;
}

function renderGoals() {
  const el = document.getElementById("dashGoals");
  const scoreEl = document.getElementById("dashGoalsScore");
  const sourceEl = document.getElementById("dashGoalsSource");
  if (!el) return;
  if (scoreEl) scoreEl.innerHTML = "";

  if (state.coverageTargets === undefined) {
    el.innerHTML = emptyHtml("Loading the goals…");
    return;
  }
  if (!state.coverageTargets?.targets || state.goals.length === 0) {
    if (scoreEl) scoreEl.textContent = "none published";
    el.innerHTML = emptyHtml("No goals published. They come from recipe-builder's data/coverage_targets.json, which its sync writes to globals/coverage_targets when it changes on main.");
    return;
  }

  const src = state.coverageTargets.source || {};
  if (sourceEl) {
    const commit = src.commit ? ` at ${escapeHtml(String(src.commit).slice(0, 7))}` : "";
    const when = state.coverageTargets.updated_at ? `, published ${escapeHtml(String(state.coverageTargets.updated_at).slice(0, 10))}` : "";
    sourceEl.innerHTML = `Set in recipe-builder's <code class="font-mono text-xs">data/coverage_targets.json</code>${commit}${when}. Measured on the whole catalog (${state.recipes.length} recipes); the filters don't apply.`;
  }

  const missed = state.goals.filter((g) => g.status !== "met");
  const met = state.goals.filter((g) => g.status === "met");
  if (scoreEl) {
    scoreEl.innerHTML = `<span><span class="${SUMMARY_NUM}">${met.length}</span> of ${state.goals.length} met</span>`;
  }

  const th = "px-3 py-2.5 text-left text-xs font-semibold text-gray-900 dark:text-white";
  const rows = missed.map((g) => {
    const dim = DIMENSION_BY_KEY[g.dimKey];
    const name = g.without
      ? `<span class="font-medium text-gray-900 dark:text-white">${escapeHtml(goalTagLabel(g))}</span>`
      : `<button type="button" data-dash-filter="${escapeAttr(g.dimKey)}" data-value="${escapeAttr(g.tag)}" class="${BUTTON.link}">${escapeHtml(goalTagLabel(g))}</button>`;
    return `
      <tr>
        <td class="py-2.5 pr-3 pl-4 sm:pl-6"><span class="${BADGE.yellow}">${g.status === "under" ? "Below" : "Above"}</span></td>
        <td class="px-3 py-2.5 text-sm"><span class="text-gray-500 dark:text-gray-400">${escapeHtml(dim.label)}</span> ${name}</td>
        <td class="px-3 py-2.5 text-right text-sm whitespace-nowrap text-gray-900 tabular-nums dark:text-white">${g.count} · ${formatShare(g.share)}</td>
        <td class="px-3 py-2.5 text-right text-sm whitespace-nowrap text-gray-500 tabular-nums dark:text-gray-400">${goalRangeText(g.min, g.max)}</td>
        <td class="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300">${escapeHtml(goalActionText(g))}</td>
        <td class="py-2.5 pr-4 pl-3 sm:pr-6">${goalMeterHtml(g)}</td>
      </tr>`;
  }).join("");

  const missedHtml = missed.length
    ? `<div class="relative overflow-x-auto"><table class="min-w-full divide-y divide-gray-200 dark:divide-white/10">
        <thead><tr><th scope="col" class="py-2.5 pr-3 pl-4 sm:pl-6"><span class="sr-only">Status</span></th><th scope="col" class="${th}">Goal</th><th scope="col" class="${th} text-right">Now</th><th scope="col" class="${th} text-right">Target</th><th scope="col" class="${th}">To meet it</th><th scope="col" class="py-2.5 pr-4 pl-3 text-left text-xs font-semibold text-gray-900 sm:pr-6 dark:text-white">Share of catalog</th></tr></thead>
        <tbody class="divide-y divide-gray-100 dark:divide-white/5">${rows}</tbody>
      </table></div>`
    : emptyHtml("Every goal is met.");

  const metHtml = met.length
    ? `<details class="group border-t border-gray-200 px-4 py-3 sm:px-6 dark:border-white/10"><summary class="flex cursor-pointer list-none items-center gap-x-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">${solidIcon("chevron-right", "size-4 text-gray-400 group-open:rotate-90")}Met (${met.length})</summary><div class="mt-3 flex flex-wrap gap-1.5">${
        met.map((g) => `<span class="${PILL}" data-tooltip="${escapeAttr(`${DIMENSION_BY_KEY[g.dimKey].label}: ${g.count} recipes, goal ${goalRangeText(g.min, g.max)}`)}">${escapeHtml(goalTagLabel(g))} <b class="font-semibold text-gray-900 dark:text-white">${Math.round(g.share * 100)}%</b> <span class="text-gray-500 dark:text-gray-400">${goalRangeText(g.min, g.max)}</span></span>`).join("")
      }</div></details>`
    : "";

  const legend = `<p class="border-t border-gray-200 px-4 py-3 text-xs text-gray-500 sm:px-6 dark:border-white/10 dark:text-gray-400">On the bars below, a tick marks a goal and ▼ / ▲ a value below or above it (hidden while filters are on).</p>`;
  el.innerHTML = missedHtml + metHtml + legend;
}

// The current share against the target range, on a 0–100% track: the band is the goal, the line is now.
function goalMeterHtml(g) {
  const pct = (x) => `${Math.max(0, Math.min(100, x * 100))}%`;
  const band = `<span class="absolute inset-y-0 rounded-full bg-green-500/35 dark:bg-green-400/30" style="left:${pct(g.min ?? 0)};right:${`${100 - Math.min(100, (g.max ?? 1) * 100)}%`}"></span>`;
  return `<span class="relative block h-2 w-40 rounded-full bg-gray-100 dark:bg-white/10" aria-hidden="true">${band}<span class="absolute -inset-y-1 w-1 -translate-x-1/2 rounded-full bg-gray-900 dark:bg-white" style="left:${pct(g.share)}"></span></span>`;
}

function renderPanels() {
  const el = document.getElementById("dashPanels");
  if (!el) return;
  const filtered = Object.keys(state.filters).length > 0;
  setSummary("dashPanelsSummary", `<span><span class="${SUMMARY_NUM}">${BAR_PANELS.length}</span> dimensions${filtered ? " · filtered" : ""}</span>`);
  el.innerHTML = BAR_PANELS.map((panel) => {
    const dim = DIMENSION_BY_KEY[panel.key];
    const base = sliceWithout(panel.key);
    const body = panel.histogram ? histogramHtml(panel.key, base) : barListHtml(panel.key, base);
    const multi = dim.multi ? `<span class="${BADGE.gray}" data-tooltip="A recipe can have several of these, so the shares add up to more than 100%.">multi</span>` : "";
    return `
      <div class="${CARD}">
        <div class="border-b border-gray-200 px-4 py-3 sm:px-6 dark:border-white/10">
          <h3 class="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">${escapeHtml(dim.label)} ${multi}</h3>
          <p class="${panel.note.includes(" ") ? NOTE : `font-mono ${NOTE}`}">${escapeHtml(panel.note)}</p>
        </div>
        ${body}
      </div>`;
  }).join("");
}

function barListHtml(dimKey, recipes) {
  const rows = countBy(recipes, dimKey);
  const selected = state.filters[dimKey];
  // Other filters can leave the chosen value with no recipes; keep it listed so it can be unchosen.
  if (selected && !rows.some((r) => r.value === selected)) rows.push({ value: selected, count: 0, share: 0 });
  if (rows.length === 0) return emptyHtml("No recipes have a value here.");
  const total = recipes.length;
  // Goals are shares of the whole catalog, so they are drawn only while the bars show the whole catalog.
  const markers = Object.keys(state.filters).length === 0 ? (state.markers[dimKey] || {}) : {};
  // A goal on a value no recipe has yet still deserves a row.
  Object.keys(markers).forEach((value) => {
    if (!rows.some((r) => r.value === value)) rows.push({ value, count: 0, share: 0 });
  });
  return `<ul class="flex flex-col gap-0.5 px-2 py-3 sm:px-4">${rows.map((row) => {
    const label = labelFor(dimKey, row.value, state.taxonomy);
    const pct = Math.round(row.share * 100);
    const dimmed = selected && selected !== row.value;
    const marker = markers[row.value];
    const ticks = marker
      ? [marker.min, marker.max].filter((x) => x !== null && x > 0 && x < 1)
          .map((x) => `<span class="absolute -inset-y-1 w-0.5 rounded-full bg-gray-900/70 dark:bg-white/80" style="left:${x * 100}%"></span>`).join("")
      : "";
    const goal = marker?.goal;
    const missed = goal && goal.status !== "met";
    const goalText = goal
      ? ` Goal (${goalTagLabel(goal)}): ${goalRangeText(goal.min, goal.max)}, ${missed ? `${goal.status === "under" ? "below" : "above"} it. ${goalActionText(goal)}.` : "met."}`
      : "";
    const flag = missed
      ? `<span class="text-xs text-amber-600 dark:text-amber-400" aria-label="${goal.status === "under" ? "Below goal" : "Above goal"}">${goal.status === "under" ? "▼" : "▲"}</span>`
      : "";
    return `
      <li>
        <button type="button" data-dash-filter="${escapeAttr(dimKey)}" data-value="${escapeAttr(row.value)}"
          aria-pressed="${selected === row.value}" ${dimmed ? "data-dimmed" : ""} ${row.count === 0 && !selected ? "disabled" : ""}
          data-tooltip="${escapeAttr(`${label}: ${row.count} of ${total} recipes (${pct}%).${goalText} Click to ${selected === row.value ? "remove the filter" : "filter by it"}.`)}"
          class="group grid w-full grid-cols-[minmax(6rem,9rem)_1fr_5rem] items-center gap-3 rounded-md px-2 py-1 text-left text-sm hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-transparent data-dimmed:opacity-40 aria-pressed:bg-paprika-50 dark:hover:bg-white/5 dark:aria-pressed:bg-paprika-500/10">
          <span class="truncate text-gray-700 dark:text-gray-300">${escapeHtml(label)}</span>
          <span class="relative h-2.5 rounded-full bg-gray-100 dark:bg-white/10"><span class="absolute inset-y-0 left-0 rounded-full bg-paprika-400 group-aria-pressed:bg-paprika-600 dark:bg-paprika-500/80 dark:group-aria-pressed:bg-paprika-400" style="width:${row.count ? Math.max(pct, 1) : 0}%"></span>${ticks}</span>
          <span class="flex items-baseline justify-end gap-1.5 tabular-nums">${flag}<span class="font-semibold text-gray-900 dark:text-white">${row.count}</span><span class="w-8 text-right text-xs text-gray-500 dark:text-gray-400">${pct}%</span></span>
        </button>
      </li>`;
  }).join("")}</ul>`;
}

function histogramHtml(dimKey, recipes) {
  const bins = timeHistogram(recipes);
  const max = Math.max(1, ...bins.map((b) => b.count));
  const selected = state.filters[dimKey];
  const total = recipes.length;
  return `
    <div class="flex h-48 items-stretch gap-1 px-4 pt-4 sm:px-6" role="list">
      ${bins.map((b) => {
        const pct = total ? Math.round((b.count / total) * 100) : 0;
        const dimmed = selected && selected !== b.value;
        return `
        <button type="button" role="listitem" data-dash-filter="${escapeAttr(dimKey)}" data-value="${escapeAttr(b.value)}"
          aria-pressed="${selected === b.value}" ${dimmed ? "data-dimmed" : ""}
          data-tooltip="${escapeAttr(`${b.label}: ${b.count} of ${total} recipes (${pct}%)`)}" ${b.count === 0 ? "disabled" : ""}
          class="group flex min-w-0 flex-1 flex-col items-center gap-1 rounded-md pt-1 hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-transparent data-dimmed:opacity-40 aria-pressed:bg-paprika-50 dark:hover:bg-white/5 dark:aria-pressed:bg-paprika-500/10">
          <span class="text-xs font-semibold text-gray-700 tabular-nums dark:text-gray-300">${b.count}</span>
          <span class="flex w-full flex-1 items-end px-0.5"><span class="w-full rounded-t-sm bg-paprika-400 group-aria-pressed:bg-paprika-600 dark:bg-paprika-500/80" style="height:${b.count ? Math.max(3, (b.count / max) * 100) : 0}%"></span></span>
          <span class="text-[0.7rem] text-gray-500 dark:text-gray-400">${escapeHtml(b.label.replace(" min", ""))}</span>
        </button>`;
      }).join("")}
    </div>
    <p class="px-4 pt-1 pb-4 text-center text-xs text-gray-500 sm:px-6 dark:text-gray-400">minutes, prep + cook</p>`;
}

function renderCrossTab(slice) {
  const rowSel = document.getElementById("dashCrossRow");
  const colSel = document.getElementById("dashCrossCol");
  const el = document.getElementById("dashCrossTab");
  if (!el) return;

  const options = (current) => CROSS_TAB_KEYS
    .map((k) => `<option value="${k}" ${k === current ? "selected" : ""}>${escapeHtml(DIMENSION_BY_KEY[k].label)}</option>`)
    .join("");
  if (rowSel) rowSel.innerHTML = options(state.crossRow);
  if (colSel) colSel.innerHTML = options(state.crossCol);
  setSummary("dashCrossSummary", `<span>${escapeHtml(DIMENSION_BY_KEY[state.crossRow].label)} × ${escapeHtml(DIMENSION_BY_KEY[state.crossCol].label)}</span>`);

  if (state.crossRow === state.crossCol) {
    el.innerHTML = emptyHtml("Pick two different dimensions.");
    return;
  }
  const { rows, cols, cells, max } = crossTab(slice, state.crossRow, state.crossCol);
  if (rows.length === 0 || cols.length === 0) {
    el.innerHTML = emptyHtml("No recipes in this selection.");
    return;
  }

  const rowLabel = (v) => labelFor(state.crossRow, v, state.taxonomy);
  const colLabel = (v) => labelFor(state.crossCol, v, state.taxonomy);
  const total = "px-2 text-center text-xs font-semibold text-gray-500 tabular-nums dark:text-gray-400";
  const head = `<tr><th scope="col" class="px-2 pb-2 text-left text-xs font-medium whitespace-nowrap text-gray-500 dark:text-gray-400">${escapeHtml(DIMENSION_BY_KEY[state.crossRow].label)} ↓ · ${escapeHtml(DIMENSION_BY_KEY[state.crossCol].label)} →</th>${
    cols.map((c) => `<th scope="col" class="px-1 pb-2 text-center text-xs font-medium whitespace-nowrap text-gray-700 dark:text-gray-300">${escapeHtml(colLabel(c.value))}</th>`).join("")
  }<th scope="col" class="${total} pb-2">Total</th></tr>`;

  // A cell's colour is its count against the largest cell: --level drives the paprika's strength.
  const body = rows.map((r) => `<tr><th scope="row" class="py-0.5 pr-3 text-left text-sm font-medium whitespace-nowrap text-gray-700 dark:text-gray-300">${escapeHtml(rowLabel(r.value))}</th>${
    cols.map((c) => {
      const n = cells[r.value]?.[c.value] || 0;
      if (n === 0) return `<td class="p-0.5 text-center text-gray-300 dark:text-gray-600" data-tooltip="${escapeAttr(`No ${rowLabel(r.value)} × ${colLabel(c.value)} recipes`)}">·</td>`;
      const level = Math.round((n / max) * 100);
      return `<td class="p-0.5"><button type="button" style="--level:${Math.max(8, level)}%" ${level > 55 ? "data-strong" : ""}
        data-dash-cell data-row="${escapeAttr(r.value)}" data-col="${escapeAttr(c.value)}"
        data-tooltip="${escapeAttr(`${rowLabel(r.value)} × ${colLabel(c.value)}: ${n} recipes. Click to filter.`)}"
        class="h-8 w-full min-w-9 rounded-md bg-paprika-500/(--level) text-xs font-semibold text-gray-900 tabular-nums hover:ring-2 hover:ring-paprika-600 data-strong:text-white dark:text-white">${n}</button></td>`;
    }).join("")
  }<td class="${total}">${r.count}</td></tr>`).join("");

  const foot = `<tr><th scope="row" class="pt-2 pr-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Total</th>${cols.map((c) => `<td class="${total} pt-2">${c.count}</td>`).join("")}<td class="${total} pt-2">${slice.length}</td></tr>`;
  el.innerHTML = `<table><thead>${head}</thead><tbody>${body}</tbody><tfoot>${foot}</tfoot></table>`;
}

function renderGaps(slice) {
  const el = document.getElementById("dashGaps");
  if (!el) return;
  setSummary("dashGapsSummary", "");
  if (Object.keys(state.taxonomy).length === 0) {
    el.innerHTML = emptyHtml("globals/categories hasn't loaded, so there is nothing to compare against.");
    return;
  }
  if (slice.length === 0) {
    el.innerHTML = emptyHtml("No recipes in this selection.");
    return;
  }
  const gaps = coverageGaps(slice, state.taxonomy);
  setSummary("dashGapsSummary", gapsSummaryHtml(gaps));
  if (gaps.length === 0) {
    el.innerHTML = emptyHtml("Every tag in Categories has at least 3 recipes here.");
    return;
  }
  el.innerHTML = `<div class="divide-y divide-gray-100 dark:divide-white/5">${gaps.map((g) => `
    <div class="flex flex-col gap-2 px-4 py-3 sm:px-6">
      <h4 class="text-sm font-semibold text-gray-900 dark:text-white">${escapeHtml(g.label)}</h4>
      ${g.missing.length ? `<div class="flex flex-wrap items-center gap-1.5"><span class="${STATUS.critical}">None</span>${
        g.missing.map((t) => `<span class="${PILL}">${escapeHtml(t.name)}</span>`).join("")}</div>` : ""}
      ${g.thin.length ? `<div class="flex flex-wrap items-center gap-1.5"><span class="${STATUS.warning}">Fewer than 3</span>${
        g.thin.map((t) => `<button type="button" data-dash-filter="${escapeAttr(g.dimKey)}" data-value="${escapeAttr(t.id)}" class="${PILL_LINK}">${escapeHtml(t.name)} <b class="font-semibold">${t.count}</b></button>`).join("")}</div>` : ""}
    </div>`).join("")}</div>`;
}

function renderQuality(slice) {
  const el = document.getElementById("dashQuality");
  if (!el) return;
  const issues = dataQuality(slice, state.taxonomy);
  setSummary("dashQualitySummary", qualitySummaryHtml(issues));
  if (issues.length === 0) {
    el.innerHTML = emptyHtml("No problems found in this selection.");
    return;
  }
  const byId = Object.fromEntries(state.recipes.map((r) => [r.id, r]));
  el.innerHTML = `<div class="divide-y divide-gray-100 dark:divide-white/5">${issues.map((issue) => {
    const badge = `<span class="${issue.severity === "warning" ? STATUS.warning : STATUS.info}">${issue.severity === "warning" ? "Fix" : "Note"}</span>`;
    const count = issue.recipeIds.length ? `<span class="ml-auto rounded-full bg-gray-100 px-2 text-xs font-medium text-gray-600 tabular-nums dark:bg-white/10 dark:text-gray-300">${issue.recipeIds.length}</span>` : "";
    const head = `${badge}<span class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(issue.title)}</span>${count}`;
    const detail = `<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">${escapeHtml(issue.detail)}</p>`;
    if (!issue.recipeIds.length) return `<div class="px-4 py-3 sm:px-6"><div class="flex items-center gap-2">${head}</div>${detail}</div>`;
    const list = issue.recipeIds.map((id) => `<li><button type="button" data-recipe-id="${escapeAttr(id)}" class="${BUTTON.link} text-left text-sm">${escapeHtml(byId[id]?.title || id)}</button></li>`).join("");
    return `<details class="group px-4 py-3 sm:px-6"><summary class="flex cursor-pointer list-none items-center gap-2">${solidIcon("chevron-right", "size-4 flex-none text-gray-400 group-open:rotate-90")}${head}</summary>${detail}<ul class="mt-2 flex flex-col gap-1 pl-6">${list}</ul></details>`;
  }).join("")}</div>`;
}

/** "3 tags with none · 5 with fewer than 3", or "none" (#1623). */
export function gapsSummaryHtml(gaps = []) {
  const missing = gaps.reduce((n, g) => n + g.missing.length, 0);
  const thin = gaps.reduce((n, g) => n + g.thin.length, 0);
  if (!missing && !thin) return `<span class="${BADGE.green}">none</span>`;
  const parts = [];
  if (missing) parts.push(`<span class="${STATUS.critical}">${missing} with none</span>`);
  if (thin) parts.push(`<span class="${STATUS.warning}">${thin} with fewer than 3</span>`);
  return parts.join("");
}

/** "2 to fix · 1 note", or "none" (#1623). */
export function qualitySummaryHtml(issues = []) {
  const fix = issues.filter((i) => i.severity === "warning").length;
  const notes = issues.length - fix;
  if (!issues.length) return `<span class="${BADGE.green}">none</span>`;
  const parts = [];
  if (fix) parts.push(`<span class="${STATUS.warning}">${fix} to fix</span>`);
  if (notes) parts.push(`<span class="${STATUS.info}">${notes} ${notes === 1 ? "note" : "notes"}</span>`);
  return parts.join("");
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const escapeAttr = escapeHtml;
